<template>
  <div class="whats-new-root">
    <!-- Permanent orb -->
    <button
      type="button"
      class="whats-orb"
      :class="{ hot: hasUnseen, idle: !hasUnseen }"
      :aria-label="hasUnseen ? 'New features available' : 'Feature changelog'"
      :title="hasUnseen ? 'New features' : 'What\'s new'"
      @click="openFeatures"
    >
      <span class="orb-core" aria-hidden="true" />
      <span class="orb-ring" aria-hidden="true" />
      <span class="orb-ring delay" aria-hidden="true" />
      <span v-if="hasUnseen" class="orb-badge">{{ unseenCount > 9 ? '9+' : unseenCount }}</span>
    </button>

    <!-- One-time intro -->
    <div v-if="showIntro" class="wn-overlay" @click.self="dismissIntro">
      <div class="wn-modal intro-modal" role="dialog" aria-labelledby="wn-intro-title">
        <div class="wn-glow" aria-hidden="true" />
        <p class="wn-kicker">Dashamajig transmission</p>
        <h2 id="wn-intro-title" class="wn-title">This app evolves at the speed of light!</h2>
        <p class="wn-body">
          You can track new features in the upper right corner. When that orb is pulsing neon, something fresh just landed. Tap it anytime.
        </p>
        <button type="button" class="wn-primary" @click="dismissIntro">Got it</button>
      </div>
    </div>

    <!-- Features modal -->
    <div v-if="showFeatures" class="wn-overlay" @click.self="closeFeatures">
      <div class="wn-modal features-modal" role="dialog" aria-labelledby="wn-features-title">
        <div class="wn-glow" aria-hidden="true" />
        <div class="wn-modal-head">
          <div>
            <p class="wn-kicker">Changelog</p>
            <h2 id="wn-features-title" class="wn-title compact">What's new</h2>
          </div>
          <button type="button" class="wn-close" aria-label="Close" @click="closeFeatures">✕</button>
        </div>

        <div class="wn-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            class="wn-tab"
            :class="{ active: tab === 'new' }"
            @click="tab = 'new'"
          >
            NEW
            <span v-if="unseenAtOpen.length" class="tab-count">{{ unseenAtOpen.length }}</span>
          </button>
          <button
            type="button"
            role="tab"
            class="wn-tab"
            :class="{ active: tab === 'all' }"
            @click="tab = 'all'"
          >
            ALL
          </button>
        </div>

        <div class="wn-list">
          <article v-for="f in visibleFeatures" :key="f.id" class="wn-card">
            <div class="wn-card-top">
              <h3>{{ f.title }}</h3>
              <time>{{ formatDate(f.date) }}</time>
            </div>
            <p>{{ f.body }}</p>
          </article>
          <p v-if="!visibleFeatures.length" class="wn-empty">
            {{ tab === 'new' ? 'You are caught up. Nothing new waiting.' : 'No entries yet.' }}
          </p>
          <p v-if="visibleFeatures.length" class="wn-bro">That's it, BRO!</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { WHATS_NEW } from '@/data/whatsNew'
import {
  hasSeenIntro,
  markIntroSeen,
  getUnseenFeatureIds,
  hasUnseenFeatures,
  markAllFeaturesSeen
} from '@/utils/whatsNewStorage'

export default {
  name: 'WhatsNewOrb',
  data() {
    return {
      showIntro: false,
      showFeatures: false,
      tab: 'new',
      hasUnseen: false,
      unseenCount: 0,
      unseenAtOpen: []
    }
  },
  computed: {
    visibleFeatures() {
      if (this.tab === 'all') return WHATS_NEW
      const set = new Set(this.unseenAtOpen)
      return WHATS_NEW.filter((f) => set.has(f.id))
    }
  },
  mounted() {
    this.refreshUnseen()
    if (!hasSeenIntro()) {
      this.showIntro = true
    }
  },
  methods: {
    refreshUnseen() {
      const ids = getUnseenFeatureIds()
      this.unseenCount = ids.length
      this.hasUnseen = hasUnseenFeatures()
    },
    dismissIntro() {
      markIntroSeen()
      this.showIntro = false
    },
    openFeatures() {
      this.unseenAtOpen = getUnseenFeatureIds()
      this.tab = this.unseenAtOpen.length ? 'new' : 'all'
      this.showFeatures = true
      // Viewing the modal counts as seen
      markAllFeaturesSeen()
      this.refreshUnseen()
    },
    closeFeatures() {
      this.showFeatures = false
      this.refreshUnseen()
    },
    formatDate(iso) {
      if (!iso) return ''
      const d = new Date(`${iso}T12:00:00`)
      if (Number.isNaN(d.getTime())) return iso
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }
}
</script>

<style scoped>
.whats-new-root {
  display: inline-flex;
  align-items: center;
}

.whats-orb {
  position: relative;
  width: 2.15rem;
  height: 2.15rem;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}
.whats-orb:hover,
.whats-orb:focus {
  transform: none;
  background: transparent;
}

.orb-core {
  position: absolute;
  inset: 18%;
  border-radius: 50%;
  background: linear-gradient(135deg, #22d3ee, #a855f7 45%, #f472b6 80%, #facc15);
  background-size: 200% 200%;
  box-shadow:
    0 0 10px rgba(34, 211, 238, 0.55),
    0 0 22px rgba(168, 85, 247, 0.45),
    inset 0 0 8px rgba(255, 255, 255, 0.35);
  z-index: 2;
}

.whats-orb.hot .orb-core {
  animation: orbGradient 2.4s ease infinite, orbPulse 1.6s ease-in-out infinite;
}

.whats-orb.idle .orb-core {
  opacity: 0.72;
  filter: saturate(0.85);
  animation: orbGradient 8s ease infinite;
  box-shadow:
    0 0 6px rgba(34, 211, 238, 0.25),
    0 0 12px rgba(168, 85, 247, 0.2);
}

.orb-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid rgba(244, 114, 182, 0.55);
  opacity: 0;
  z-index: 1;
  pointer-events: none;
}

.whats-orb.hot .orb-ring {
  animation: orbRipple 2s ease-out infinite;
}
.whats-orb.hot .orb-ring.delay {
  animation-delay: 1s;
}

.orb-badge {
  position: absolute;
  top: -0.25rem;
  right: -0.35rem;
  min-width: 1.05rem;
  height: 1.05rem;
  padding: 0 0.25rem;
  border-radius: 999px;
  background: #f472b6;
  color: #1a1025;
  font-size: 0.65rem;
  font-weight: 800;
  line-height: 1.05rem;
  text-align: center;
  z-index: 3;
  box-shadow: 0 0 10px rgba(244, 114, 182, 0.8);
}

@keyframes orbGradient {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes orbPulse {
  0%, 100% { transform: scale(1); filter: brightness(1); }
  50% { transform: scale(1.12); filter: brightness(1.25); }
}
@keyframes orbRipple {
  0% { transform: scale(0.7); opacity: 0.7; }
  100% { transform: scale(1.55); opacity: 0; }
}

.wn-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background:
    radial-gradient(ellipse at 70% 20%, rgba(168, 85, 247, 0.28), transparent 45%),
    radial-gradient(ellipse at 20% 80%, rgba(34, 211, 238, 0.2), transparent 40%),
    rgba(6, 4, 18, 0.78);
  backdrop-filter: blur(6px);
}

.wn-modal {
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: 86vh;
  overflow: auto;
  padding: 1.5rem 1.55rem 1.35rem;
  border-radius: 18px;
  color: #fff;
  background: linear-gradient(165deg, rgba(28, 18, 48, 0.97), rgba(12, 10, 28, 0.98));
  border: 1px solid rgba(244, 114, 182, 0.35);
  box-shadow:
    0 0 40px rgba(168, 85, 247, 0.25),
    0 24px 60px rgba(0, 0, 0, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.wn-glow {
  position: absolute;
  top: -40%;
  right: -20%;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(34, 211, 238, 0.22), transparent 70%);
  pointer-events: none;
  animation: glowDrift 6s ease-in-out infinite alternate;
}
@keyframes glowDrift {
  from { transform: translate(0, 0); opacity: 0.7; }
  to { transform: translate(-30px, 40px); opacity: 1; }
}

.wn-kicker {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #f9a8d4;
  font-weight: 700;
}
.wn-title {
  margin: 0 0 0.85rem;
  font-size: 1.55rem;
  line-height: 1.2;
  background: linear-gradient(100deg, #67e8f9, #e879f9 50%, #fde047);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.wn-title.compact {
  margin-bottom: 0;
  font-size: 1.35rem;
}
.wn-body {
  margin: 0 0 1.25rem;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.55;
  font-size: 0.98rem;
}
.wn-primary {
  width: 100%;
  margin: 0;
  padding: 0.7rem 1rem;
  border: none;
  border-radius: 999px;
  font-weight: 800;
  color: #12081f;
  cursor: pointer;
  background: linear-gradient(110deg, #22d3ee, #c084fc, #f472b6);
  background-size: 180% 180%;
  animation: orbGradient 4s ease infinite;
  box-shadow: 0 0 24px rgba(168, 85, 247, 0.45);
}
.wn-primary:hover,
.wn-primary:focus {
  transform: translateY(-1px) scale(1.01);
  background: linear-gradient(110deg, #22d3ee, #c084fc, #f472b6);
}

.wn-modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.9rem;
  position: relative;
  z-index: 1;
}
.wn-close {
  margin: 0;
  padding: 0.25rem 0.5rem;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
  cursor: pointer;
}
.wn-close:hover {
  color: #fff;
  background: transparent;
  transform: none;
}

.wn-tabs {
  display: flex;
  gap: 0.35rem;
  margin-bottom: 0.9rem;
  padding: 0.25rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
  position: relative;
  z-index: 1;
}
.wn-tab {
  flex: 1;
  margin: 0;
  padding: 0.45rem 0.75rem;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 800;
  letter-spacing: 0.08em;
  font-size: 0.78rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
}
.wn-tab:hover {
  background: rgba(255, 255, 255, 0.05);
  transform: none;
}
.wn-tab.active {
  color: #12081f;
  background: linear-gradient(110deg, #67e8f9, #e879f9);
  box-shadow: 0 0 16px rgba(232, 121, 249, 0.35);
}
.tab-count {
  min-width: 1.1rem;
  padding: 0.05rem 0.3rem;
  border-radius: 999px;
  background: rgba(18, 8, 31, 0.25);
  font-size: 0.7rem;
}

.wn-list {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}
.wn-card {
  padding: 0.85rem 0.95rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(167, 139, 250, 0.22);
  box-shadow: inset 0 0 20px rgba(34, 211, 238, 0.04);
}
.wn-card-top {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: baseline;
  margin-bottom: 0.35rem;
}
.wn-card h3 {
  margin: 0;
  font-size: 1rem;
  color: #f5d0fe;
}
.wn-card time {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.45);
  white-space: nowrap;
}
.wn-card p {
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.45;
  font-size: 0.9rem;
}
.wn-empty {
  margin: 1rem 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
  font-style: italic;
}
.wn-bro {
  margin: 0.35rem 0 0.15rem;
  text-align: center;
  font-weight: 900;
  font-size: 1.05rem;
  letter-spacing: 0.04em;
  background: linear-gradient(100deg, #fde047, #f472b6, #22d3ee);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 0 24px rgba(244, 114, 182, 0.35);
}
</style>
